import type {
  BootstrapResponse,
  ConfirmedSnapshot,
  DailyPurchaseDefault,
  Dish,
  DishType,
  ExtraPurchaseItem,
  ExtraPurchaseSelection,
  FixedPurchaseCustomSnapshot,
  FixedPurchaseDishSnapshot,
  FixedPurchaseIngredientSnapshot,
  FixedPurchaseOccurrence,
  FixedPurchaseOverride,
  FixedPurchaseOverridesResponse,
  FixedPurchaseRule,
  FixedPurchaseRuleInput,
  FixedPurchaseRuleMutationResponse,
  FixedPurchaseRulesResponse,
  FixedPurchaseScheduleType,
  FixedPurchaseSourceSnapshot,
  FixedPurchaseSourceType,
  IngredientCatalogItem,
  IngredientLine,
  Meal,
  PlanDay,
  PlanSelection,
  PublicOrder,
  PurchaseItem,
  ResolvedFixedPurchases,
  WechatPurchaseMessage,
  WeeklyPlan,
} from '../types';
import { createWeekDays, isCalendarConfigured, weekEnd } from './date';
import {
  DEFAULT_DAILY_PURCHASE_DEFAULTS,
  normalizeIngredientCategory,
} from './ingredient-categories';

type JsonObject = Record<string, unknown>;

export class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

function object(value: unknown): JsonObject {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as JsonObject) : {};
}

function array(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function string(value: unknown, fallback = ''): string {
  return value === undefined || value === null ? fallback : String(value);
}

function bool(value: unknown, fallback = false): boolean {
  if (value === undefined || value === null) return fallback;
  return value === true || value === 1 || value === '1';
}

function number(value: unknown, fallback = 0): number {
  const result = Number(value);
  return Number.isFinite(result) ? result : fallback;
}

function pick(row: JsonObject, camel: string, snake?: string): unknown {
  return row[camel] ?? (snake ? row[snake] : undefined);
}

function normalizeIngredient(value: unknown): IngredientLine {
  const row = object(value);
  const name = string(row.name ?? row.ingredientName ?? row.ingredient_name);
  return {
    id: string(row.id) || undefined,
    name,
    quantity: string(row.quantity, '0'),
    unit: string(row.unit),
    ingredientCategory: normalizeIngredientCategory(
      row.ingredientCategory ?? row.ingredient_category ?? row.category,
      name,
    ),
  };
}

function normalizeDailyPurchaseDefault(value: unknown): DailyPurchaseDefault {
  const row = object(value);
  const name = string(row.name ?? row.ingredientName ?? row.ingredient_name);
  return {
    id: string(row.id ?? row.ingredientId ?? row.ingredient_id) || undefined,
    name,
    quantity: string(row.quantity, '0'),
    unit: string(row.unit),
    weekdayMask: number(row.weekdayMask ?? row.weekday_mask, 127),
    ingredientCategory: normalizeIngredientCategory(
      row.ingredientCategory ?? row.ingredient_category ?? row.category,
      name,
    ),
  };
}

function normalizeIngredientCatalogItem(value: unknown): IngredientCatalogItem {
  const row = object(value);
  const name = string(row.name ?? row.ingredientName ?? row.ingredient_name);
  return {
    id: string(row.id ?? row.ingredientId ?? row.ingredient_id),
    name,
    unit: string(row.unit),
    ingredientCategory: normalizeIngredientCategory(
      row.ingredientCategory ?? row.ingredient_category ?? row.category,
      name,
    ),
  };
}

function fixedSourceType(value: unknown): FixedPurchaseSourceType {
  const normalized = string(value);
  if (normalized === 'dish' || normalized === 'custom') return normalized;
  return 'ingredient';
}

function fixedScheduleType(value: unknown): FixedPurchaseScheduleType {
  const normalized = string(value);
  if (normalized === 'weekdays' || normalized === 'weekly') return normalized;
  return 'service_day';
}

function normalizeFixedPurchaseSourceSnapshot(
  value: unknown,
  fallback: {
    sourceType: FixedPurchaseSourceType;
    sourceId?: string;
    name: string;
    unit?: string;
    ingredientCategory?: unknown;
  },
): FixedPurchaseSourceSnapshot {
  const row = object(value);
  const sourceType = fixedSourceType(row.sourceType ?? row.source_type ?? fallback.sourceType);
  if (sourceType === 'dish') {
    return {
      sourceType: 'dish',
      dishId: string(row.dishId ?? row.dish_id ?? fallback.sourceId),
      dishName: string(row.dishName ?? row.dish_name ?? row.name, fallback.name),
      type: string(row.type) === 'condiment_restock' ? 'condiment_restock' : 'normal',
      category: string(row.category) || undefined,
      standardPeople: number(row.standardPeople ?? row.standard_people, 50),
      ingredients: array(row.ingredients).map(normalizeIngredient),
    } satisfies FixedPurchaseDishSnapshot;
  }
  const name = string(row.name, fallback.name);
  const unit = string(row.unit, fallback.unit);
  const ingredientCategory = normalizeIngredientCategory(
    row.ingredientCategory ?? row.ingredient_category ?? row.category ?? fallback.ingredientCategory,
    name,
  );
  if (sourceType === 'custom') {
    return {
      sourceType: 'custom',
      name,
      unit,
      ingredientCategory,
    } satisfies FixedPurchaseCustomSnapshot;
  }
  return {
    sourceType: 'ingredient',
    ingredientId: string(row.ingredientId ?? row.ingredient_id ?? fallback.sourceId),
    name,
    unit,
    ingredientCategory,
  } satisfies FixedPurchaseIngredientSnapshot;
}

export function normalizeFixedPurchaseRule(value: unknown): FixedPurchaseRule {
  const row = object(value);
  const sourceType = fixedSourceType(pick(row, 'sourceType', 'source_type'));
  const sourceId = string(pick(row, 'sourceId', 'source_id')) || undefined;
  const name = string(row.name ?? row.sourceName ?? row.source_name);
  const unit = string(row.unit) || undefined;
  const ingredientCategory = sourceType === 'dish'
    ? undefined
    : normalizeIngredientCategory(row.ingredientCategory ?? row.ingredient_category, name);
  return {
    id: string(row.id),
    revisionId: string(pick(row, 'revisionId', 'revision_id')),
    revision: Math.max(1, number(row.revision, 1)),
    effectiveFromWeek: string(pick(row, 'effectiveFromWeek', 'effective_from_week')),
    active: bool(row.active, true),
    sourceType,
    sourceId,
    name,
    unit,
    ingredientCategory,
    quantity: string(row.quantity) || undefined,
    batchCount: sourceType === 'dish'
      ? Math.max(1, number(pick(row, 'batchCount', 'batch_count'), 1))
      : undefined,
    scheduleType: fixedScheduleType(pick(row, 'scheduleType', 'schedule_type')),
    weekdayMask: row.weekdayMask !== undefined || row.weekday_mask !== undefined
      ? number(pick(row, 'weekdayMask', 'weekday_mask'), 127)
      : undefined,
    sortOrder: number(pick(row, 'sortOrder', 'sort_order'), 0),
    sourceSnapshot: normalizeFixedPurchaseSourceSnapshot(
      pick(row, 'sourceSnapshot', 'source_snapshot'),
      { sourceType, sourceId, name, unit, ingredientCategory },
    ),
  };
}

function normalizeFixedPurchaseOverride(value: unknown): FixedPurchaseOverride {
  const row = object(value);
  return {
    ruleId: string(pick(row, 'ruleId', 'rule_id')),
    date: string(row.date) || undefined,
    enabled: row.enabled === undefined ? undefined : bool(row.enabled),
    quantity: string(row.quantity) || undefined,
    batchCount: row.batchCount !== undefined || row.batch_count !== undefined
      ? Math.max(1, number(pick(row, 'batchCount', 'batch_count'), 1))
      : undefined,
  };
}

function normalizeFixedPurchaseOccurrence(value: unknown): FixedPurchaseOccurrence {
  const row = object(value);
  const sourceType = fixedSourceType(pick(row, 'sourceType', 'source_type'));
  const sourceId = string(pick(row, 'sourceId', 'source_id')) || undefined;
  const name = string(row.name);
  const unit = string(row.unit) || undefined;
  const ingredientCategory = sourceType === 'dish'
    ? undefined
    : normalizeIngredientCategory(row.ingredientCategory ?? row.ingredient_category, name);
  return {
    ruleId: string(pick(row, 'ruleId', 'rule_id')),
    revisionId: string(pick(row, 'revisionId', 'revision_id')),
    revision: Math.max(1, number(row.revision, 1)),
    date: string(row.date) || undefined,
    sourceType,
    sourceId,
    name,
    unit,
    ingredientCategory,
    quantity: string(row.quantity) || undefined,
    batchCount: sourceType === 'dish'
      ? Math.max(1, number(pick(row, 'batchCount', 'batch_count'), 1))
      : undefined,
    scheduleType: fixedScheduleType(pick(row, 'scheduleType', 'schedule_type')),
    weekdayMask: row.weekdayMask !== undefined || row.weekday_mask !== undefined
      ? number(pick(row, 'weekdayMask', 'weekday_mask'), 127)
      : undefined,
    sourceSnapshot: normalizeFixedPurchaseSourceSnapshot(
      pick(row, 'sourceSnapshot', 'source_snapshot'),
      { sourceType, sourceId, name, unit, ingredientCategory },
    ),
    adjusted: bool(row.adjusted),
  };
}

function normalizeResolvedFixedPurchases(value: unknown): ResolvedFixedPurchases {
  const row = object(value);
  return {
    daily: array(row.daily).map(normalizeFixedPurchaseOccurrence),
    weekly: array(row.weekly).map(normalizeFixedPurchaseOccurrence),
    skipped: array(row.skipped).map((entry) => {
      const skipped = object(entry);
      const rawReason = string(skipped.reason);
      const reason = rawReason === 'paused' || rawReason === 'non_service_day'
        ? rawReason
        : 'week_override';
      return {
        ruleId: string(pick(skipped, 'ruleId', 'rule_id')),
        revisionId: string(pick(skipped, 'revisionId', 'revision_id')),
        date: string(skipped.date) || undefined,
        name: string(skipped.name),
        reason,
      };
    }),
  };
}

export function normalizeDish(value: unknown): Dish {
  const row = object(value);
  const rawType = string(row.type, 'normal');
  return {
    id: string(row.id),
    name: string(row.name),
    type: (rawType === 'condiment_restock' ? rawType : 'normal') as DishType,
    category: string(row.category) || undefined,
    standardPeople: number(pick(row, 'standardPeople', 'standard_people'), 50),
    active: bool(row.active, true),
    ingredients: array(row.ingredients ?? row.recipe).map(normalizeIngredient),
  };
}

function normalizeSelection(value: unknown): PlanSelection {
  const row = object(value);
  const snapshot = object(pick(row, 'recipeSnapshot', 'recipe_snapshot'));
  const meal = string(row.meal, 'breakfast') === 'lunch' ? 'lunch' : 'breakfast';
  return {
    id: string(row.id) || undefined,
    dishId: string(pick(row, 'dishId', 'dish_id')),
    meal: meal as Meal,
    batchCount: Math.max(1, number(pick(row, 'batchCount', 'batch_count'), 1)),
    recipeSnapshot: Object.keys(snapshot).length
      ? {
          dishName: string(pick(snapshot, 'dishName', 'dish_name')),
          ingredients: array(snapshot.ingredients).map(normalizeIngredient),
        }
      : undefined,
  };
}

function normalizeDay(value: unknown): PlanDay {
  const row = object(value);
  const source = string(row.source || row.overrideSource || row.override_source, 'default');
  const legacyService = bool(pick(row, 'serviceEnabled', 'service_enabled'), true);
  const rawMealService = object(pick(row, 'mealService', 'meal_service'));
  const mealService = {
    breakfast: bool(rawMealService.breakfast, legacyService),
    lunch: bool(rawMealService.lunch, legacyService),
  };
  return {
    date: string(row.date),
    mealService,
    serviceEnabled: mealService.breakfast || mealService.lunch,
    source: source === 'manual' || source === 'official' ? source : 'default',
    label: string(row.label) || undefined,
    selections: array(row.selections).map(normalizeSelection),
  };
}

function normalizeExtraSelection(value: unknown): ExtraPurchaseSelection {
  const row = object(value);
  const snapshot = object(pick(row, 'recipeSnapshot', 'recipe_snapshot'));
  return {
    id: string(row.id) || undefined,
    dishId: string(pick(row, 'dishId', 'dish_id')),
    batchCount: Math.max(1, number(pick(row, 'batchCount', 'batch_count'), 1)),
    recipeSnapshot: Object.keys(snapshot).length
      ? {
          dishName: string(pick(snapshot, 'dishName', 'dish_name')),
          ingredients: array(snapshot.ingredients).map(normalizeIngredient),
        }
      : undefined,
  };
}

function normalizeExtraItem(value: unknown): ExtraPurchaseItem {
  const row = object(value);
  const name = string(row.name ?? row.ingredientName ?? row.ingredient_name);
  const sourceType = string(pick(row, 'sourceType', 'source_type')) === 'ingredient'
    ? 'ingredient'
    : 'custom';
  return {
    id: string(row.id) || undefined,
    sourceType,
    ingredientId: string(pick(row, 'ingredientId', 'ingredient_id')) || undefined,
    name,
    quantity: string(row.quantity, '0'),
    unit: string(row.unit),
    ingredientCategory: normalizeIngredientCategory(
      row.ingredientCategory ?? row.ingredient_category ?? row.category,
      name,
    ),
  };
}

export function emptyPlan(weekStart: string): WeeklyPlan {
  return {
    weekStart,
    weekEnd: weekEnd(weekStart),
    status: 'draft',
    version: 0,
    calendarConfigured: isCalendarConfigured(weekStart),
    days: createWeekDays(weekStart),
    extraSelections: [],
    extraItems: [],
    dailyPurchaseOverrides: {},
    purchaseOverrides: {},
    fixedPurchaseOverrides: [],
  };
}

export function normalizePlan(value: unknown, requestedWeek: string): WeeklyPlan {
  const row = object(value);
  const fallback = emptyPlan(requestedWeek);
  const weekStart = string(pick(row, 'weekStart', 'week_start'), requestedWeek);
  const days = array(row.days).map(normalizeDay);
  const rawOverrides = object(pick(row, 'purchaseOverrides', 'purchase_overrides'));
  const rawDailyOverrides = object(pick(row, 'dailyPurchaseOverrides', 'daily_purchase_overrides'));
  const extraSelections = array(pick(row, 'extraSelections', 'extra_selections')).map(normalizeExtraSelection);
  const extraItems = array(pick(row, 'extraItems', 'extra_items')).map(normalizeExtraItem);
  const fixedPurchaseOverrides = array(
    pick(row, 'fixedPurchaseOverrides', 'fixed_purchase_overrides'),
  ).map(normalizeFixedPurchaseOverride);
  const resolvedValue = pick(row, 'resolvedFixedPurchases', 'resolved_fixed_purchases');
  const purchaseOverrides = Object.fromEntries(
    Object.entries(rawOverrides).map(([key, quantity]) => [key, string(quantity)]),
  );
  const dailyPurchaseOverrides = Object.fromEntries(
    Object.entries(rawDailyOverrides).map(([key, quantity]) => [key, string(quantity)]),
  );
  return {
    id: string(row.id) || undefined,
    weekStart,
    weekEnd: string(pick(row, 'weekEnd', 'week_end'), weekEnd(weekStart)),
    status: string(row.status, 'draft') === 'confirmed' ? 'confirmed' : 'draft',
    version: number(row.version, 0),
    calendarConfigured: bool(pick(row, 'calendarConfigured', 'calendar_configured'), isCalendarConfigured(weekStart)),
    days: days.length ? days : fallback.days,
    extraSelections,
    extraItems,
    dailyPurchaseOverrides,
    purchaseOverrides,
    fixedPurchaseOverrides,
    resolvedFixedPurchases: resolvedValue
      ? normalizeResolvedFixedPurchases(resolvedValue)
      : undefined,
    fixedPurchaseRulesRevision: row.fixedPurchaseRulesRevision !== undefined
      || row.fixed_purchase_rules_revision !== undefined
      ? number(pick(row, 'fixedPurchaseRulesRevision', 'fixed_purchase_rules_revision'))
      : undefined,
    updatedAt: string(pick(row, 'updatedAt', 'updated_at')) || undefined,
  };
}

function normalizePurchaseItem(value: unknown): PurchaseItem {
  const row = object(value);
  const name = string(row.name ?? row.ingredientName ?? row.ingredient_name);
  const unit = string(row.unit);
  const calculated = string(pick(row, 'calculatedQuantity', 'calculated_quantity'), string(row.quantity, '0'));
  return {
    key: string(row.key, `${name}\u0000${unit}`),
    name,
    unit,
    calculatedQuantity: calculated,
    finalQuantity: string(pick(row, 'finalQuantity', 'final_quantity'), calculated),
    ingredientCategory: normalizeIngredientCategory(
      row.ingredientCategory ?? row.ingredient_category ?? row.category,
      name,
    ),
    sources: array(row.sources).map((sourceValue) => {
      const source = object(sourceValue);
      const sourceKind = string(source.kind);
      return {
        kind: sourceKind === 'extra' || sourceKind === 'daily_default' ? sourceKind : 'meal' as const,
        date: string(source.date) || undefined,
        meal: source.meal === 'lunch' ? 'lunch' as const : source.meal === 'breakfast' ? 'breakfast' as const : undefined,
        dishId: string(pick(source, 'dishId', 'dish_id')),
        dishName: string(pick(source, 'dishName', 'dish_name')),
        batchCount: number(pick(source, 'batchCount', 'batch_count'), 1),
        contributionQuantity: string(pick(source, 'contributionQuantity', 'contribution_quantity'), '0'),
        unit: string(source.unit, unit),
      };
    }),
  };
}

function normalizeWechatPurchaseMessage(value: unknown): WechatPurchaseMessage | undefined {
  const row = object(value);
  const kind = string(row.kind);
  if (kind !== 'meat' && kind !== 'vegetable' && kind !== 'condiment') return undefined;
  return {
    kind,
    title: string(row.title),
    text: string(row.text),
  };
}

export function normalizeSnapshot(value: unknown): ConfirmedSnapshot {
  const row = object(value);
  const hasWechatMessages = 'wechatMessages' in row || 'wechat_messages' in row;
  const hasDailyGroups = 'dailyPurchaseGroups' in row || 'daily_purchase_groups' in row;
  const hasExtraPurchaseItems = 'extraPurchaseItems' in row || 'extra_purchase_items' in row;
  const rawDailyGroups = array(pick(row, 'dailyPurchaseGroups', 'daily_purchase_groups'));
  const dailyPurchaseGroups = rawDailyGroups.map((groupValue) => {
    const group = object(groupValue);
    return {
      date: string(group.date),
      items: array(group.items ?? group.purchaseItems ?? group.purchase_items).map(normalizePurchaseItem),
    };
  }).filter((group) => group.date && group.items.length);
  const extraPurchaseItems = array(pick(row, 'extraPurchaseItems', 'extra_purchase_items')).map(normalizePurchaseItem);
  const rawPurchaseItems = array(row.purchaseItems ?? row.purchase_items ?? row.items).map(normalizePurchaseItem);
  const purchaseItems = rawPurchaseItems.length
    ? rawPurchaseItems
    : [...dailyPurchaseGroups.flatMap((group) => group.items), ...extraPurchaseItems];
  const flatSelections = array(row.selections).length
    ? array(row.selections)
    : array(row.days).flatMap((dayValue) => {
        const day = object(dayValue);
        return array(day.selections).map((selectionValue) => {
          const selection = object(selectionValue);
          return { ...selection, date: selection.date ?? day.date };
        });
      });
  return {
    planId: string(pick(row, 'planId', 'plan_id'), string(row.id)),
    kitchenName: string(pick(row, 'kitchenName', 'kitchen_name'), '龙庭公司厨房'),
    weekStart: string(pick(row, 'weekStart', 'week_start')),
    weekEnd: string(pick(row, 'weekEnd', 'week_end')),
    version: number(row.version, 1),
    confirmedAt: string(pick(row, 'confirmedAt', 'confirmed_at')) || undefined,
    dailyPurchaseGroups: hasDailyGroups ? dailyPurchaseGroups : undefined,
    extraPurchaseItems: hasDailyGroups || hasExtraPurchaseItems ? extraPurchaseItems : undefined,
    purchaseItems,
    selections: flatSelections.map((value) => {
      const selection = object(value);
      const recipe = object(pick(selection, 'recipeSnapshot', 'recipe_snapshot'));
      return {
        date: string(selection.date),
        meal: (string(selection.meal) === 'lunch' ? 'lunch' : 'breakfast') as Meal,
        dishName: string(pick(selection, 'dishName', 'dish_name'), string(pick(recipe, 'dishName', 'dish_name'))),
        batchCount: number(pick(selection, 'batchCount', 'batch_count'), 1),
        ingredients: array(selection.ingredients ?? recipe.ingredients).map(normalizeIngredient),
      };
    }),
    extraSelections: array(pick(row, 'extraSelections', 'extra_selections')).map((value) => {
      const selection = object(value);
      const recipe = object(pick(selection, 'recipeSnapshot', 'recipe_snapshot'));
      return {
        dishName: string(pick(selection, 'dishName', 'dish_name'), string(pick(recipe, 'dishName', 'dish_name'))),
        batchCount: number(pick(selection, 'batchCount', 'batch_count'), 1),
        ingredients: array(selection.ingredients ?? recipe.ingredients).map(normalizeIngredient),
      };
    }),
    extraItems: array(pick(row, 'extraItems', 'extra_items')).map(normalizeExtraItem),
    wechatText: string(row.wechatText ?? row.wechat_text ?? row.wechatCopy ?? row.wechat_copy),
    ...(hasWechatMessages
      ? {
          wechatMessages: array(pick(row, 'wechatMessages', 'wechat_messages'))
            .map(normalizeWechatPurchaseMessage)
            .filter((message): message is WechatPurchaseMessage => message !== undefined),
        }
      : {}),
  };
}

export function normalizeBootstrap(value: unknown, week: string): BootstrapResponse {
  const row = object(value);
  const plan = normalizePlan(row.plan, week);
  const snapshotValue = row.latestSnapshot ?? row.latest_snapshot ?? row.snapshot;
  const calendarConfigured = bool(
    pick(row, 'calendarConfigured', 'calendar_configured'),
    plan.calendarConfigured ?? isCalendarConfigured(week),
  );
  plan.calendarConfigured = calendarConfigured;
  const hasDailyDefaults = 'dailyPurchaseDefaults' in row || 'daily_purchase_defaults' in row;
  const dailyPurchaseDefaults = hasDailyDefaults
    ? array(pick(row, 'dailyPurchaseDefaults', 'daily_purchase_defaults')).map(normalizeDailyPurchaseDefault)
    : DEFAULT_DAILY_PURCHASE_DEFAULTS.map((item) => ({ ...item }));
  const ingredients = array(row.ingredients).map(normalizeIngredientCatalogItem);
  const fixedPurchaseRules = array(
    pick(row, 'fixedPurchaseRules', 'fixed_purchase_rules'),
  ).map(normalizeFixedPurchaseRule);
  const fixedPurchaseRulesRevision = row.fixedPurchaseRulesRevision !== undefined
    || row.fixed_purchase_rules_revision !== undefined
    ? number(pick(row, 'fixedPurchaseRulesRevision', 'fixed_purchase_rules_revision'))
    : undefined;
  if (fixedPurchaseRulesRevision !== undefined) {
    plan.fixedPurchaseRulesRevision = fixedPurchaseRulesRevision;
  }
  return {
    kitchenName: string(pick(row, 'kitchenName', 'kitchen_name'), '龙庭公司厨房'),
    dishes: array(row.dishes).map(normalizeDish),
    ingredients,
    plan,
    calendarConfigured,
    dailyPurchaseDefaults,
    fixedPurchaseRules,
    fixedPurchaseRulesRevision,
    fixedPurchaseDefaultEffectiveFromWeek: string(
      pick(row, 'fixedPurchaseDefaultEffectiveFromWeek', 'fixed_purchase_default_effective_from_week'),
    ) || undefined,
    latestSnapshot: snapshotValue ? normalizeSnapshot(snapshotValue) : undefined,
  };
}

function normalizePublicOrder(value: unknown): PublicOrder {
  const snapshot = normalizeSnapshot(object(value).order ?? value);
  return {
    kitchenName: snapshot.kitchenName,
    weekStart: snapshot.weekStart,
    weekEnd: snapshot.weekEnd,
    version: snapshot.version,
    dailyPurchaseGroups: snapshot.dailyPurchaseGroups,
    extraPurchaseItems: snapshot.extraPurchaseItems,
    purchaseItems: snapshot.purchaseItems,
    wechatText: snapshot.wechatText || undefined,
    ...(snapshot.wechatMessages ? { wechatMessages: snapshot.wechatMessages } : {}),
    confirmedAt: snapshot.confirmedAt,
  };
}

async function request<T>(path: string, token?: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body) headers.set('Content-Type', 'application/json');
  if (token) headers.set('Authorization', `Bearer ${token}`);
  headers.set('Accept', 'application/json');

  let response: Response;
  try {
    response = await fetch(path, { ...init, headers });
  } catch {
    throw new ApiError('无法连接服务器，请检查网络后重试', 0);
  }

  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    payload = undefined;
  }

  const envelope = object(payload);
  if (!response.ok || envelope.ok === false) {
    const error = object(envelope.error);
    const message = string(error.message ?? envelope.error, response.status === 401 ? '私密链接无效或已失效' : '操作失败，请重试');
    throw new ApiError(message, response.status);
  }
  return ((envelope.ok === true && 'data' in envelope ? envelope.data : payload) ?? {}) as T;
}

export function readFragmentToken(name: 'access' | 'share'): string {
  const fragment = window.location.hash.replace(/^#/, '');
  const params = new URLSearchParams(fragment);
  return params.get(name)?.trim() ?? '';
}

export async function getBootstrap(token: string, week: string): Promise<BootstrapResponse> {
  const data = await request<unknown>(`/api/bootstrap?week=${encodeURIComponent(week)}`, token);
  return normalizeBootstrap(data, week);
}

export async function savePlan(token: string, plan: WeeklyPlan): Promise<WeeklyPlan> {
  const data = await request<unknown>(`/api/plans/${encodeURIComponent(plan.weekStart)}`, token, {
    method: 'PUT',
    body: JSON.stringify(plan),
  });
  return normalizePlan(object(data).plan ?? data, plan.weekStart);
}

function normalizeFixedPurchaseRulesResponse(value: unknown): FixedPurchaseRulesResponse {
  const row = object(value);
  return {
    weekStart: string(pick(row, 'weekStart', 'week_start')),
    defaultEffectiveFromWeek: string(
      pick(row, 'defaultEffectiveFromWeek', 'default_effective_from_week'),
    ),
    rulesRevision: number(pick(row, 'rulesRevision', 'rules_revision')),
    rules: array(row.rules).map(normalizeFixedPurchaseRule),
  };
}

function normalizeFixedPurchaseMutation(value: unknown): FixedPurchaseRuleMutationResponse {
  const row = object(value);
  return {
    rulesRevision: number(pick(row, 'rulesRevision', 'rules_revision')),
    rule: normalizeFixedPurchaseRule(row.rule ?? row),
  };
}

export async function getFixedPurchaseRules(
  token: string,
  week?: string,
): Promise<FixedPurchaseRulesResponse> {
  const query = week ? `?week=${encodeURIComponent(week)}` : '';
  const data = await request<unknown>(`/api/fixed-purchase-rules${query}`, token);
  return normalizeFixedPurchaseRulesResponse(data);
}

export async function createFixedPurchaseRule(
  token: string,
  input: FixedPurchaseRuleInput,
): Promise<FixedPurchaseRuleMutationResponse> {
  const data = await request<unknown>('/api/fixed-purchase-rules', token, {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return normalizeFixedPurchaseMutation(data);
}

export async function updateFixedPurchaseRule(
  token: string,
  ruleId: string,
  input: Partial<FixedPurchaseRuleInput> & { expectedRevision: number },
): Promise<FixedPurchaseRuleMutationResponse> {
  const data = await request<unknown>(
    `/api/fixed-purchase-rules/${encodeURIComponent(ruleId)}`,
    token,
    { method: 'PUT', body: JSON.stringify(input) },
  );
  return normalizeFixedPurchaseMutation(data);
}

export async function pauseFixedPurchaseRule(
  token: string,
  ruleId: string,
  expectedRevision: number,
  effectiveFromWeek?: string,
): Promise<FixedPurchaseRuleMutationResponse> {
  const data = await request<unknown>(
    `/api/fixed-purchase-rules/${encodeURIComponent(ruleId)}/pause`,
    token,
    {
      method: 'POST',
      body: JSON.stringify({ expectedRevision, effectiveFromWeek }),
    },
  );
  return normalizeFixedPurchaseMutation(data);
}

export async function saveFixedPurchaseOverrides(
  token: string,
  week: string,
  overrides: FixedPurchaseOverride[],
): Promise<FixedPurchaseOverridesResponse> {
  const data = await request<unknown>(
    `/api/plans/${encodeURIComponent(week)}/fixed-purchase-overrides`,
    token,
    { method: 'PUT', body: JSON.stringify({ overrides }) },
  );
  const row = object(data);
  return {
    weekStart: string(pick(row, 'weekStart', 'week_start'), week),
    planId: string(pick(row, 'planId', 'plan_id')),
    status: string(row.status) === 'confirmed' ? 'confirmed' : 'draft',
    rulesRevision: number(pick(row, 'rulesRevision', 'rules_revision')),
    overrides: array(row.overrides).map(normalizeFixedPurchaseOverride),
    resolvedFixedPurchases: normalizeResolvedFixedPurchases(
      pick(row, 'resolvedFixedPurchases', 'resolved_fixed_purchases'),
    ),
  };
}

export async function confirmPlan(token: string, plan: WeeklyPlan): Promise<ConfirmedSnapshot> {
  const target = plan.id ?? plan.weekStart;
  const data = await request<unknown>(`/api/plans/${encodeURIComponent(target)}/confirm`, token, {
    method: 'POST',
    body: JSON.stringify({ expectedVersion: plan.version }),
  });
  return normalizeSnapshot(object(data).snapshot ?? data);
}

export async function createShareLink(token: string, planId: string): Promise<{ url?: string; shareToken?: string }> {
  const data = await request<JsonObject>(`/api/plans/${encodeURIComponent(planId)}/share`, token, { method: 'POST' });
  return {
    url: string(data.url) || undefined,
    shareToken: string(pick(data, 'shareToken', 'share_token')) || undefined,
  };
}

export async function fetchExportData(token: string, planId: string): Promise<ConfirmedSnapshot> {
  const data = await request<unknown>(`/api/plans/${encodeURIComponent(planId)}/export-data`, token);
  return normalizeSnapshot(object(data).snapshot ?? data);
}

export async function createDish(token: string, dish: Omit<Dish, 'id'>): Promise<Dish> {
  const data = await request<unknown>('/api/dishes', token, { method: 'POST', body: JSON.stringify(dish) });
  return normalizeDish(object(data).dish ?? data);
}

export async function updateDish(token: string, dish: Dish): Promise<Dish> {
  const data = await request<unknown>(`/api/dishes/${encodeURIComponent(dish.id)}`, token, {
    method: 'PUT',
    body: JSON.stringify(dish),
  });
  return normalizeDish(object(data).dish ?? data);
}

export async function getPublicOrder(token: string): Promise<PublicOrder> {
  const data = await request<unknown>('/api/public/order', token);
  return normalizePublicOrder(data);
}
